import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, take } from 'rxjs';
import { ResponseItem } from '@models/response-item.model';
import { ToastServiceManager } from '../services/toast-service-manager.service';

/**
 * Base class for "item" components that create/edit a single entity
 * (premise hazards, premise hazard types, etc.) via a form.
 * @template T The type of entity being created/edited
 */
@Component({
  template: '' // Abstract component, no template
})
export abstract class BaseItemComponent<T extends { id: string }> {
  @Input() ETag?: string;

  @Output() submittedEvent: EventEmitter<ResponseItem<T>> = new EventEmitter<ResponseItem<T>>();
  @Output() cancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() conflictErrorEvent: EventEmitter<void> = new EventEmitter<void>();

  public IsCreating: boolean = false;
  public IsSubmitting: boolean = false;

  constructor(protected readonly toastServiceManager: ToastServiceManager) {}

  public cancelClicked(): void {
    this.cancelEvent.emit();
  }

  /**
   * Validates the form and flips into the "submitting" state if it's valid.
   * Returns false (marking all controls touched and showing an error toast)
   * if the form is invalid or a submission is already in progress.
   */
  protected beginSubmit(form: FormGroup, incompleteMessage: string): boolean {
    if (form.invalid || this.IsSubmitting) {
      form.markAllAsTouched();
      this.toastServiceManager.error(incompleteMessage);
      return false;
    }
    this.IsSubmitting = true;
    return true;
  }

  /**
   * Common success/error handling for a create/update submission request:
   * shows a success toast if the response contains an item, emits
   * `submittedEvent`, and resets `IsSubmitting` if the request fails.
   */
  protected handleSubmitResponse(response$: Observable<ResponseItem<T>>, successMessage: string): void {
    response$.pipe(take(1)).subscribe({
      next: (response) => {
        if (response.Item) {
          this.toastServiceManager.success(successMessage);
        }
        this.submittedEvent.emit(response);
      },
      error: () => {
        this.IsSubmitting = false;
      },
    });
  }
}
